import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useToast } from '../../../context/ToastContext';
import { useConfirm } from '../../../context/ConfirmContext';
import Select from '../../../components/ui/Select';
import { fetchRoleCatalog, fetchRoleCountries } from '../../../services/masterDataApi';
import { apiRequest } from '../../../services/apiClient';

const UNIT_OPTIONS = ['Kilogram (kg)', 'Ton', 'Çuval', 'Kasa'];
const PRODUCT_FEATURE_OPTIONS = ['Organik', 'İyi Tarım Uygulamaları', 'Tropikal'];
const MAX_PRODUCT_IMAGES = 5;

const normalizeImageUrls = (urls = []) =>
  Array.from(new Set((Array.isArray(urls) ? urls : []).map((url) => `${url || ''}`.trim()).filter(Boolean)))
    .slice(0, MAX_PRODUCT_IMAGES);

const INITIAL_FORM_DATA = {
  productGroup: '',
  productName: '',
  productType: '',
  quantity: '',
  unit: 'Kilogram (kg)',
  pricePerUnit: '',
  harvestDate: '',
  availableDate: '',
  allowPartialSale: false,
  openToOffer: false,
  productFeatures: [],
  city: '',
  country: '',
  farmLocation: '',
  productDescription: '',
};

const FormSection = ({ title, children }) => (
  <fieldset className="rounded-xl border border-slate-300/30 px-4 pt-2 pb-4 md:px-5">
    <legend className="px-2 text-lg font-medium text-text-primary">{title}</legend>
    <div className="pt-1">{children}</div>
  </fieldset>
);

const QuickProductForm = ({
  onClose,
  onSubmit,
  mode = 'create',
  initialValues = null,
  onEditImagesPersist,
}) => {
  const { error: toastError, info: toastInfo, success: toastSuccess } = useToast();
  const { confirm } = useConfirm();
  const isEditMode = mode === 'edit';
  const imagePreviewUrlsRef = useRef(new Set());
  const [productCatalog, setProductCatalog] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState('');
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productImages, setProductImages] = useState([]);
  const [editImageUrls, setEditImageUrls] = useState([]);
  const [isPersistingImages, setIsPersistingImages] = useState(false);
  const [activeImageSlot, setActiveImageSlot] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadCatalog = async () => {
      setIsCatalogLoading(true);
      try {
        const [catalogResponse, countriesResponse] = await Promise.all([
          fetchRoleCatalog(),
          fetchRoleCountries(),
        ]);

        const parsedCatalog = {};
        (catalogResponse?.groups || []).forEach((group) => {
          if (!group?.name) {
            return;
          }

          const nameMap = {};
          (group.productNames || []).forEach((productName) => {
            if (!productName?.name) {
              return;
            }

            const parsedTypes = (productName.productTypes || [])
              .map((productType) => productType?.name)
              .filter(Boolean);

            nameMap[productName.name] = parsedTypes.length ? parsedTypes : ['Genel'];
          });

          parsedCatalog[group.name] = nameMap;
        });

        const parsedCountries = (countriesResponse?.countries || [])
          .map((country) => country?.name)
          .filter(Boolean);

        if (!Object.keys(parsedCatalog).length) {
          throw new Error('Katalog verisi bulunamadı.');
        }

        if (isMounted) {
          setProductCatalog(parsedCatalog);
          setCountryOptions(parsedCountries);
          setFormData((prev) => ({
            ...prev,
            country: prev.country || parsedCountries[0] || '',
          }));
          setCatalogError('');
        }
      } catch (error) {
        console.error('Katalog verisi yüklenirken hata oluştu:', error);
        if (isMounted) {
          setCatalogError('Katalog verisi yüklenemedi. Lütfen superadmin ayarlarını kontrol edin.');
          toastError('Katalog verisi yüklenemedi. Ürün alanları görüntülenemiyor.');
        }
      } finally {
        if (isMounted) {
          setIsCatalogLoading(false);
        }
      }
    };

    loadCatalog();

    return () => {
      isMounted = false;
    };
  }, [toastError]);

  useEffect(() => {
    if (!isEditMode || !initialValues || isCatalogLoading) {
      return;
    }

    setFormData((prev) => ({
      ...INITIAL_FORM_DATA,
      ...prev,
      ...initialValues,
      quantity: initialValues?.quantity ?? '',
      pricePerUnit: initialValues?.pricePerUnit ?? '',
      productFeatures: Array.isArray(initialValues?.productFeatures)
        ? initialValues.productFeatures
        : [],
      country:
        initialValues?.country ||
        prev.country ||
        countryOptions[0] ||
        '',
    }));
  }, [countryOptions, initialValues, isCatalogLoading, isEditMode]);

  useEffect(() => () => {
    imagePreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    imagePreviewUrlsRef.current.clear();
  }, []);

  useEffect(() => {
    if (!isEditMode) {
      setEditImageUrls([]);
      return;
    }

    const baseUrls = [];
    if (Array.isArray(initialValues?.existingImageUrls)) {
      baseUrls.push(...initialValues.existingImageUrls);
    }
    if (initialValues?.existingImageUrl) {
      baseUrls.push(initialValues.existingImageUrl);
    }

    setEditImageUrls(normalizeImageUrls(baseUrls));
  }, [initialValues?.existingImageUrl, initialValues?.existingImageUrls, isEditMode]);

  const productGroups = useMemo(() => Object.keys(productCatalog), [productCatalog]);
  const productNames = useMemo(
    () => Object.keys(productCatalog[formData.productGroup] || {}),
    [formData.productGroup, productCatalog],
  );
  const productTypes = useMemo(
    () => productCatalog[formData.productGroup]?.[formData.productName] || [],
    [formData.productGroup, formData.productName, productCatalog],
  );
  const productNameOptions = useMemo(
    () => productNames.map((name) => ({ label: name, value: name })),
    [productNames],
  );
  const productTypeOptions = useMemo(
    () => productTypes.map((type) => ({ label: type, value: type })),
    [productTypes],
  );
  const productFeatureOptions = useMemo(() => {
    const normalizedOptions = new Set(PRODUCT_FEATURE_OPTIONS);
    const selectedFeatures = Array.isArray(formData.productFeatures) ? formData.productFeatures : [];
    const initialFeatures = Array.isArray(initialValues?.productFeatures)
      ? initialValues.productFeatures
      : [];

    [...initialFeatures, ...selectedFeatures]
      .map((feature) => `${feature || ''}`.trim())
      .filter(Boolean)
      .forEach((feature) => normalizedOptions.add(feature));

    return Array.from(normalizedOptions).map((feature) => ({
      label: feature,
      value: feature,
    }));
  }, [formData.productFeatures, initialValues?.productFeatures]);
  const resolvedEditImageUrls = useMemo(
    () => normalizeImageUrls(editImageUrls),
    [editImageUrls],
  );

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (name === 'productGroup') {
      setFormData((prev) => ({
        ...prev,
        productGroup: value,
        productName: '',
        productType: '',
      }));
      setErrors((prev) => ({ ...prev, productGroup: '', productName: '', productType: '' }));
      return;
    }

    if (name === 'productName') {
      setFormData((prev) => ({
        ...prev,
        productName: value,
        productType: '',
      }));
      setErrors((prev) => ({ ...prev, productName: '', productType: '' }));
      return;
    }

    updateField(name, type === 'checkbox' ? checked : value);
  };

  const handleProductNameSelect = (value) => {
    setFormData((prev) => ({
      ...prev,
      productName: value,
      productType: '',
    }));
    setErrors((prev) => ({ ...prev, productName: '', productType: '' }));
  };

  const handleProductTypeSelect = (value) => {
    updateField('productType', value);
  };

  const handleProductFeaturesChange = (values) => {
    updateField('productFeatures', values);
  };

  const handleSwitchToggle = (fieldName) => {
    updateField(fieldName, !formData[fieldName]);
  };

  const uploadImages = async (files = []) => {
    const uploadFiles = Array.isArray(files)
      ? files.filter((file) => file instanceof File).slice(0, MAX_PRODUCT_IMAGES)
      : [];

    if (!uploadFiles.length) {
      return [];
    }

    const body = new FormData();
    uploadFiles.forEach((file) => {
      body.append('files', file);
    });

    const result = await apiRequest('/market/products/upload-images', {
      method: 'POST',
      auth: true,
      body,
    });

    return normalizeImageUrls(result?.imageUrls);
  };

  const persistEditImages = async (nextUrls, successMessage) => {
    const normalizedNextUrls = normalizeImageUrls(nextUrls);
    if (typeof onEditImagesPersist !== 'function') {
      setEditImageUrls(normalizedNextUrls);
      toastInfo('Görsel değişikliği yerelde işlendi. Kaydetmek için "Ürünü Güncelle" kullanın.');
      return;
    }

    setIsPersistingImages(true);
    try {
      const updatedProduct = await onEditImagesPersist(normalizedNextUrls);
      const updatedUrls = normalizeImageUrls(updatedProduct?.images || normalizedNextUrls);
      setEditImageUrls(updatedUrls);
      if (successMessage) {
        toastSuccess(successMessage);
      }
    } finally {
      setIsPersistingImages(false);
      setActiveImageSlot(null);
    }
  };

  const handleProductImageUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) {
      return;
    }

    if (isEditMode) {
      const filesToUpload = selectedFiles.slice(0, MAX_PRODUCT_IMAGES);
      if (selectedFiles.length > MAX_PRODUCT_IMAGES) {
        toastInfo(`Sadece ilk ${MAX_PRODUCT_IMAGES} görsel yüklendi.`);
      }

      try {
        const uploadedUrls = await uploadImages(filesToUpload);
        if (!uploadedUrls.length) {
          toastError('Görsel yüklenemedi.');
          return;
        }

        const nextUrls = [...resolvedEditImageUrls];
        uploadedUrls.forEach((url, index) => {
          nextUrls[index] = url;
        });

        await persistEditImages(nextUrls, `${uploadedUrls.length} görsel kaydedildi.`);
      } catch (error) {
        toastError(error?.message || 'Görseller yüklenirken hata oluştu.');
      } finally {
        event.target.value = '';
      }
      return;
    }

    const remainingSlots = MAX_PRODUCT_IMAGES - productImages.length;
    if (remainingSlots <= 0) {
      toastInfo(`En fazla ${MAX_PRODUCT_IMAGES} ürün görseli ekleyebilirsiniz.`);
      event.target.value = '';
      return;
    }

    const filesToAdd = selectedFiles.slice(0, remainingSlots);
    if (selectedFiles.length > remainingSlots) {
      toastInfo(`Sadece ilk ${remainingSlots} görsel eklendi.`);
    }

    const mappedFiles = filesToAdd.map((file, index) => {
      const preview = URL.createObjectURL(file);
      imagePreviewUrlsRef.current.add(preview);
      return {
        id: `${Date.now()}-${index}`,
        file,
        preview,
      };
    });

    setProductImages((prev) => [...prev, ...mappedFiles]);
    event.target.value = '';
  };

  const handleEditSlotUpload = async (slotIndex, event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';

    if (!selectedFile) {
      return;
    }

    setActiveImageSlot(slotIndex);
    try {
      const [uploadedUrl] = await uploadImages([selectedFile]);
      if (!uploadedUrl) {
        toastError('Görsel yüklenemedi.');
        return;
      }

      const nextUrls = [...resolvedEditImageUrls];
      if (slotIndex < resolvedEditImageUrls.length) {
        nextUrls[slotIndex] = uploadedUrl;
      } else {
        nextUrls.push(uploadedUrl);
      }

      await persistEditImages(nextUrls, 'Görsel kaydedildi.');
    } catch (error) {
      toastError(error?.message || 'Görsel güncellenirken hata oluştu.');
      setActiveImageSlot(null);
    }
  };

  const handleEditImageDelete = async (slotIndex) => {
    const targetUrl = resolvedEditImageUrls[slotIndex];
    if (!targetUrl) {
      return;
    }

    const shouldDelete = await confirm({
      title: 'Görsel silinsin mi?',
      message: 'Bu görsel ürün kaydından kaldırılacak.',
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      tone: 'danger',
    });

    if (!shouldDelete) {
      return;
    }

    setActiveImageSlot(slotIndex);
    try {
      const nextUrls = resolvedEditImageUrls.filter((_, index) => index !== slotIndex);
      await persistEditImages(nextUrls, 'Görsel silindi.');
    } catch (error) {
      toastError(error?.message || 'Görsel silinirken hata oluştu.');
      setActiveImageSlot(null);
    }
  };

  const removeProductImage = (imageId) => {
    setProductImages((prev) => {
      const imageToRemove = prev.find((image) => image.id === imageId);
      if (imageToRemove?.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
        imagePreviewUrlsRef.current.delete(imageToRemove.preview);
      }
      return prev.filter((image) => image.id !== imageId);
    });
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.productGroup) nextErrors.productGroup = 'Ürün grubu zorunludur.';
    if (!formData.productName) nextErrors.productName = 'Ürün adı zorunludur.';
    if (!formData.productType) nextErrors.productType = 'Ürün türü zorunludur.';
    if (!formData.quantity || Number(formData.quantity) <= 0) nextErrors.quantity = 'Miktar 0\'dan büyük olmalıdır.';
    if (!formData.pricePerUnit || Number(formData.pricePerUnit) <= 0) nextErrors.pricePerUnit = 'Birim fiyat 0\'dan büyük olmalıdır.';
    if (!formData.availableDate) nextErrors.availableDate = 'Satışa uygunluk tarihi zorunludur.';
    if (!formData.city?.trim()) nextErrors.city = 'Şehir zorunludur.';
    if (!formData.country) nextErrors.country = 'Ülke zorunludur.';

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit?.({
        ...formData,
        productImages: isEditMode ? [] : productImages.map((image) => image.file),
      });
    } catch (error) {
      console.error('Ürün oluşturulurken hata oluştu:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    imagePreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    imagePreviewUrlsRef.current.clear();
    onClose?.();
  };

  const inputClassName = (name) =>
    `w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 bg-background text-text-primary ${
      errors[name] ? 'border-error focus:border-error' : 'border-border focus:border-border-focus'
    }`;

  const renderError = (name) =>
    errors[name] ? <p className="text-error text-xs mt-1">{errors[name]}</p> : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-elevation-5 w-full max-w-6xl max-h-[92vh] overflow-hidden">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-text-primary font-heading">
              {isEditMode ? 'Ürünü Düzenle' : 'Yeni Ürün Oluştur'}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(92vh-140px)]">
          <form id="quick-product-form" onSubmit={handleSubmit} className="p-6 space-y-6">
            <FormSection title="Ürün Bilgileri">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="productGroup">
                    Ürün Grubu <span className="text-error">*</span>
                  </label>
                  <select
                    id="productGroup"
                    name="productGroup"
                    value={formData.productGroup}
                    onChange={handleInputChange}
                    className={inputClassName('productGroup')}
                    disabled={isCatalogLoading}
                  >
                    <option value="">Seçiniz</option>
                    {productGroups.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                  {catalogError ? <p className="text-warning text-xs mt-1">{catalogError}</p> : null}
                  {renderError('productGroup')}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="productName">
                    Ürün Adı <span className="text-error">*</span>
                  </label>
                  <Select
                    id="productName"
                    name="productName"
                    value={formData.productName || ''}
                    onChange={handleProductNameSelect}
                    options={productNameOptions}
                    placeholder="Seçiniz"
                    searchable
                    searchPlaceholder="Ürün adı ara..."
                    emptySearchText="Eşleşen ürün bulunamadı"
                    emptyOptionsText="Ürün listesi boş"
                    className="w-full"
                    disabled={!formData.productGroup}
                  />
                  {renderError('productName')}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="productType">
                    Ürün Türü <span className="text-error">*</span>
                  </label>
                  <Select
                    id="productType"
                    name="productType"
                    value={formData.productType || ''}
                    onChange={handleProductTypeSelect}
                    options={productTypeOptions}
                    placeholder="Seçiniz"
                    searchable
                    searchPlaceholder="Ürün türü ara..."
                    emptySearchText="Eşleşen tür bulunamadı"
                    emptyOptionsText="Tür listesi boş"
                    className="w-full"
                    disabled={!formData.productName}
                  />
                  {renderError('productType')}
                </div>
              </div>
            </FormSection>

            <FormSection title="Fiyat Bilgileri">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="quantity">
                    Miktar <span className="text-error">*</span>
                  </label>
                  <input
                    id="quantity"
                    name="quantity"
                    type="number"
                    min="0"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    className={inputClassName('quantity')}
                    placeholder="0"
                  />
                  {renderError('quantity')}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="unit">
                    Birim
                  </label>
                  <select
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    className={inputClassName('unit')}
                  >
                    {UNIT_OPTIONS.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="pricePerUnit">
                    Birim Fiyat <span className="text-error">*</span>
                  </label>
                  <input
                    id="pricePerUnit"
                    name="pricePerUnit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.pricePerUnit}
                    onChange={handleInputChange}
                    className={inputClassName('pricePerUnit')}
                    placeholder="0.00"
                  />
                  {renderError('pricePerUnit')}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="harvestDate">
                    Hasat Tarihi
                  </label>
                  <input
                    id="harvestDate"
                    name="harvestDate"
                    type="date"
                    value={formData.harvestDate}
                    onChange={handleInputChange}
                    className={inputClassName('harvestDate')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="availableDate">
                    Satışa Uygunluk Tarihi <span className="text-error">*</span>
                  </label>
                  <input
                    id="availableDate"
                    name="availableDate"
                    type="date"
                    value={formData.availableDate}
                    onChange={handleInputChange}
                    className={inputClassName('availableDate')}
                  />
                  {renderError('availableDate')}
                </div>

                <div className="rounded-md border border-border bg-background p-3 space-y-3">
                  <div className="flex items-center justify-between text-sm font-medium text-text-primary">
                    <span>Kısmi Satış</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.allowPartialSale}
                      aria-label="Kısmi Satış"
                      onClick={() => handleSwitchToggle('allowPartialSale')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        formData.allowPartialSale ? 'bg-primary' : 'bg-secondary-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          formData.allowPartialSale ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-sm font-medium text-text-primary">
                    <span>Teklife Açık</span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={formData.openToOffer}
                      aria-label="Teklife Açık"
                      onClick={() => handleSwitchToggle('openToOffer')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                        formData.openToOffer ? 'bg-primary' : 'bg-secondary-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          formData.openToOffer ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </FormSection>

            <FormSection title="Ürün Özellikleri">
              <div className="space-y-3">
                <Select
                  id="productFeatures"
                  name="productFeatures"
                  value={formData.productFeatures}
                  onChange={handleProductFeaturesChange}
                  options={productFeatureOptions}
                  placeholder="Özellik seçiniz"
                  searchable
                  multiple
                  clearable
                  showSelectedChips
                  searchPlaceholder="Özellik ara..."
                  emptySearchText="Eşleşen özellik bulunamadı"
                  emptyOptionsText="Özellik listesi boş"
                  className="w-full"
                />
              </div>
            </FormSection>

            <FormSection title="Satış Konumu (Şehir ve Ülke)">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="city">
                    Şehir <span className="text-error">*</span>
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={inputClassName('city')}
                    placeholder="Örn. Konya"
                  />
                  {renderError('city')}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="country">
                    Ülke <span className="text-error">*</span>
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={inputClassName('country')}
                    disabled={!countryOptions.length}
                  >
                    <option value="">Seçiniz</option>
                    {countryOptions.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  {renderError('country')}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="farmLocation">
                    Tarla Konumu (Referans)
                  </label>
                  <input
                    id="farmLocation"
                    name="farmLocation"
                    type="text"
                    value={formData.farmLocation}
                    onChange={handleInputChange}
                    className={inputClassName('farmLocation')}
                    placeholder="Örn. Çiftlik A - Tarla 1"
                  />
                </div>
              </div>
            </FormSection>

            <FormSection title="Ek Detaylar (Opsiyonel)">
              <label className="block text-sm font-medium text-text-primary mb-1" htmlFor="productDescription">
                Ürün Açıklaması
              </label>
              <textarea
                id="productDescription"
                name="productDescription"
                rows={4}
                value={formData.productDescription}
                onChange={handleInputChange}
                className={inputClassName('productDescription')}
                placeholder="Ürününüzü açıklayın..."
              />
            </FormSection>

            <FormSection title="Ürün Resimleri">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-text-secondary">
                  {isEditMode
                    ? `Görseller bu alanda anında kaydedilir (maks. ${MAX_PRODUCT_IMAGES}).`
                    : `En fazla ${MAX_PRODUCT_IMAGES} görsel ekleyebilirsiniz.`}
                </p>
                <div>
                  <input
                    type="file"
                    id="product-image-upload"
                    className="hidden"
                    multiple
                    accept="image/*"
                    onChange={handleProductImageUpload}
                    disabled={isPersistingImages}
                  />
                  <label
                    htmlFor="product-image-upload"
                    className={`inline-flex items-center gap-2 px-3 py-2 text-sm border border-border rounded-md transition-colors duration-200 ${
                      isPersistingImages
                        ? 'cursor-not-allowed opacity-60'
                        : 'cursor-pointer hover:border-primary hover:text-primary'
                    }`}
                  >
                    {isPersistingImages ? (
                      <Icon name="Loader2" size={16} className="animate-spin" />
                    ) : (
                      <Icon name="Upload" size={16} />
                    )}
                    {isEditMode ? 'Toplu Değiştir' : 'Görsel Yükle'}
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Array.from({ length: MAX_PRODUCT_IMAGES }).map((_, index) => {
                  if (isEditMode) {
                    const slotInputId = `edit-product-image-slot-${index}`;
                    const currentImageUrl = resolvedEditImageUrls[index];
                    const isSlotBusy = isPersistingImages && activeImageSlot === index;

                    return (
                      <div
                        key={`edit-slot-${index + 1}`}
                        className="border border-border rounded-md overflow-hidden bg-background"
                      >
                        <input
                          id={slotInputId}
                          type="file"
                          className="hidden"
                          accept="image/*"
                          disabled={isPersistingImages}
                          onChange={(event) => handleEditSlotUpload(index, event)}
                        />

                        {currentImageUrl ? (
                          <>
                            <label
                              htmlFor={slotInputId}
                              className={`relative block h-28 ${isPersistingImages ? 'cursor-wait' : 'cursor-pointer'}`}
                            >
                              <img
                                src={currentImageUrl}
                                alt={`Ürün görseli ${index + 1}`}
                                className="h-full w-full object-cover"
                              />
                              {isSlotBusy && (
                                <span className="absolute inset-0 bg-black/30 flex items-center justify-center text-white">
                                  <Icon name="Loader2" size={18} className="animate-spin" />
                                </span>
                              )}
                            </label>
                            <div className="grid grid-cols-2 gap-1 p-1 border-t border-border">
                              <button
                                type="button"
                                onClick={() => handleEditImageDelete(index)}
                                disabled={isPersistingImages}
                                className="inline-flex items-center justify-center gap-1 rounded-md border border-error/30 text-error text-xs py-1.5 hover:bg-error/10 disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200"
                              >
                                <Icon name="Trash2" size={12} />
                                Sil
                              </button>
                              <label
                                htmlFor={slotInputId}
                                className={`inline-flex items-center justify-center gap-1 rounded-md border text-xs py-1.5 transition-colors duration-200 ${
                                  isPersistingImages
                                    ? 'cursor-not-allowed opacity-60 border-border text-text-secondary'
                                    : 'cursor-pointer border-primary/40 text-primary hover:bg-primary/10'
                                }`}
                              >
                                <Icon name="Upload" size={12} />
                                Değiştir
                              </label>
                            </div>
                          </>
                        ) : (
                          <label
                            htmlFor={slotInputId}
                            className={`h-36 border border-dashed border-border flex flex-col items-center justify-center text-text-secondary transition-colors duration-200 ${
                              isPersistingImages
                                ? 'cursor-not-allowed opacity-60'
                                : 'cursor-pointer hover:border-primary hover:text-primary'
                            }`}
                          >
                            <Icon name="Upload" size={18} />
                            <span className="text-xs mt-1">Görsel {index + 1}</span>
                          </label>
                        )}
                      </div>
                    );
                  }

                  const currentImage = productImages[index];
                  if (currentImage) {
                    return (
                      <div
                        key={currentImage.id}
                        className="relative border border-border rounded-md overflow-hidden h-28"
                      >
                        <img
                          src={currentImage.preview}
                          alt={`Ürün görseli ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeProductImage(currentImage.id)}
                          className="absolute top-1 right-1 bg-black bg-opacity-60 text-white rounded-full p-1 hover:bg-opacity-80 transition-colors duration-200"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <label
                      key={`empty-slot-${index + 1}`}
                      htmlFor="product-image-upload"
                      className="border border-dashed border-border rounded-md h-28 flex flex-col items-center justify-center text-text-secondary cursor-pointer hover:border-primary hover:text-primary transition-colors duration-200"
                    >
                      <Icon name="Upload" size={18} />
                      <span className="text-xs mt-1">Görsel {index + 1}</span>
                    </label>
                  );
                })}
              </div>
            </FormSection>
          </form>
        </div>

        <div className="p-6 border-t border-border">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              form="quick-product-form"
              disabled={isSubmitting || isPersistingImages}
              className="bg-primary text-white px-6 py-2 rounded-md font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ease-out micro-interaction"
            >
              {(isSubmitting || isPersistingImages)
                ? isEditMode ? 'Güncelleniyor...' : 'Kaydediliyor...'
                : isEditMode ? 'Ürünü Güncelle' : 'Yeni Ürün Oluştur'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickProductForm;
